import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const SORTS = [
  { id: "popular", label: "Most Popular" },
  { id: "price_low", label: "Price: Low to High" },
  { id: "price_high", label: "Price: High to Low" },
  { id: "rating", label: "Top Rated" },
];

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "all";
  const searchQuery = searchParams.get("search") || "";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sort, setSort] = useState("popular");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (categoryParam !== "all") params.category = categoryParam;
    if (searchQuery) params.search = searchQuery;
    api
      .get("/products", { params })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [categoryParam, searchQuery]);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "price_low") arr.sort((a, b) => a.price_discounted - b.price_discounted);
    else if (sort === "price_high") arr.sort((a, b) => b.price_discounted - a.price_discounted);
    else if (sort === "rating") arr.sort((a, b) => b.rating - a.rating);
    else arr.sort((a, b) => b.reviews_count - a.reviews_count);
    return arr;
  }, [products, sort]);

  const setCategory = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") next.delete("category");
    else next.set("category", id);
    next.delete("search");
    setSearchParams(next);
  };

  const activeCatName =
    categoryParam === "all" ? "All Products" : categories.find((c) => c.id === categoryParam)?.name || "Shop";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 className="font-serif font-bold text-3xl sm:text-4xl text-[#1C2B20]" data-testid="shop-title">
          {searchQuery ? `Results for "${searchQuery}"` : activeCatName}
        </h1>
        <p className="mt-2 text-[#5A6B5D]">{sorted.length} product{sorted.length !== 1 ? "s" : ""} available</p>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        {/* Sidebar filter */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="bg-white rounded-2xl border border-[#E2DAD0] p-5">
            <div className="flex items-center gap-2 mb-4 text-[#1C2B20] font-semibold">
              <SlidersHorizontal size={18} /> Categories
            </div>
            <div className="flex flex-wrap lg:flex-col gap-2" data-testid="category-filter">
              <button
                onClick={() => setCategory("all")}
                data-testid="filter-all"
                className={`text-left text-sm rounded-lg px-3 py-2 transition-colors ${
                  categoryParam === "all" ? "bg-[#1F4D2E] text-white font-medium" : "text-[#5A6B5D] hover:bg-[#F5F0E6]"
                }`}
              >
                All Products
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  data-testid={`filter-${c.id}`}
                  className={`text-left text-sm rounded-lg px-3 py-2 transition-colors ${
                    categoryParam === c.id ? "bg-[#1F4D2E] text-white font-medium" : "text-[#5A6B5D] hover:bg-[#F5F0E6]"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Products */}
        <div>
          <div className="flex items-center justify-end mb-6">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="sort-select"
              className="bg-white border border-[#E2DAD0] rounded-full px-4 py-2 text-sm text-[#1C2B20] focus:outline-none focus:ring-2 focus:ring-[#1F4D2E]"
            >
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="text-center py-20 text-[#8A9A8D]">Loading products...</div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-20 text-[#8A9A8D]" data-testid="shop-empty">
              No products found in this category.
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {sorted.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
